import React, { ReactNode, useMemo } from 'react';

import PropTypes from 'prop-types';
import type { CSSObject } from 'styled-components';

import { ArrowRightIcon, CalendarFilledIcon } from '@adsk/alloy-react-icon';
import { mergeTokenStyles, useTheme } from '@adsk/alloy-react-styles';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

import FieldValue from './FieldValue';
import ToggleIcon from './ToggleIcon';

type FieldProps = StylableComponent<HTMLButtonElement> & {
  open?: boolean;
  value?:
    | string
    | null
    | [string | null | undefined, string | null | undefined];
  startDatePlaceholder?: string;
  endDatePlaceholder?: string;
  disabled?: boolean;
  error?: boolean;
  range?: boolean;
  placeholder?: ReactNode;
};

const Field = React.forwardRef<HTMLButtonElement, FieldProps>(
  (
    {
      open,
      value,
      placeholder,
      startDatePlaceholder,
      endDatePlaceholder,
      disabled,
      error,
      range,
      style,
      className,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const t = theme.tokens?.component?.datePicker;

    const fieldStyle = useMemo(() => {
      if (!t?.field) {
        return undefined;
      }

      const parts: CSSObject[] = [t.field._ as CSSObject];

      if (error && t.field.error?.true) {
        parts.push(t.field.error.true as CSSObject);
      }

      if (disabled && t.field.disabled?.true) {
        parts.push(t.field.disabled.true as CSSObject);
      }

      return mergeTokenStyles(...parts);
    }, [t, error, disabled]);

    const [startDate, endDate] = Array.isArray(value) ? value : [value];
    const rangeDatesSelected = range ? startDate && endDate : false;

    const fieldValueStyle = {
      display: 'block',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as const,
    };

    return (
      <button
        type="button"
        ref={ref}
        className={className}
        disabled={disabled}
        css={[fieldStyle, style ?? undefined]}
        {...props}
      >
        <CalendarFilledIcon size={16} />
        <FieldValue
          value={startDate}
          disabled={disabled}
          selecting={open && !rangeDatesSelected}
          placeholder={range ? startDatePlaceholder : placeholder}
          data-testid="DatePickerFieldValue-start"
          style={fieldValueStyle}
        />
        {range && (
          <>
            <ArrowRightIcon />
            <FieldValue
              value={endDate}
              disabled={disabled}
              selecting={open && !rangeDatesSelected}
              placeholder={endDatePlaceholder}
              data-testid="DatePickerFieldValue-end"
              style={fieldValueStyle}
            />
          </>
        )}
        <ToggleIcon isOpen={!!open} />
      </button>
    );
  },
);

Field.displayName = 'Field';

Field.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The value to display, should be the formatted date(s) string(s) */
  value: PropTypes.oneOfType([PropTypes.any, PropTypes.string]),
  /** Placeholder to display when there is no value */
  placeholder: PropTypes.string,
  /** Passed to the field, startDatePlaceholder to display when there is no value */
  startDatePlaceholder: PropTypes.string,
  /** Passed to the field, endDatePlaceHolder to display when there is no value */
  endDatePlaceholder: PropTypes.string,
  /** Indicates whether the overlay is open */
  open: PropTypes.bool,
  /** Whether we should display range state */
  range: PropTypes.bool,
  /** Whether the field is disabled */
  disabled: PropTypes.bool,
  /** Whether the field should display error state */
  error: PropTypes.bool,
};

export default Field;
